import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functionsEurope } from '../firebase';
import {
  UsersIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarDaysIcon,
  CpuChipIcon,
  ServerIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  PuzzlePieceIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const AnalyticsWidget = () => {
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('campaigns'); // Default to campaigns
  const [selectedPeriod, setSelectedPeriod] = useState('today'); // Default to today
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load all analytics data
  const loadAnalytics = async (period = selectedPeriod) => {
    setIsLoading(true);
    setError(null);

    try {
      const [userAnalyticsFunc, campaignAnalyticsFunc, systemHealthFunc] = [
        httpsCallable(functionsEurope, 'getUserAnalytics'),
        httpsCallable(functionsEurope, 'getCampaignAnalytics'),
        httpsCallable(functionsEurope, 'getSystemHealth')
      ];

      const [userResult, campaignResult, healthResult] = await Promise.all([
        userAnalyticsFunc(),
        campaignAnalyticsFunc({ period }),
        systemHealthFunc()
      ]);

      setUserAnalytics(userResult.data);
      setCampaignAnalytics(campaignResult.data);
      setSystemHealth(healthResult.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError('Failed to load analytics: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Force refresh analytics cache
  const forceRefresh = async () => {
    setIsRefreshing(true);
    setError(null);

    try {
      const refreshFunc = httpsCallable(functionsEurope, 'refreshAnalyticsCache');
      await refreshFunc();
      
      // After cache refresh, reload the data
      await loadAnalytics();
    } catch (error) {
      console.error('Error refreshing cache:', error);
      setError('Failed to refresh cache: ' + error.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  // Reload analytics when period changes
  useEffect(() => {
    if (activeTab === 'campaigns') {
      loadAnalytics(selectedPeriod);
    }
  }, [selectedPeriod]);

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
    const colorMap = {
      blue: { bg: 'rgba(52, 152, 219, 0.2)', border: 'rgba(52, 152, 219, 0.4)', icon: '#3498DB' },
      green: { bg: 'rgba(46, 204, 113, 0.2)', border: 'rgba(46, 204, 113, 0.4)', icon: '#2ECC71' },
      purple: { bg: 'rgba(155, 89, 182, 0.2)', border: 'rgba(155, 89, 182, 0.4)', icon: '#9B59B6' },
      orange: { bg: 'rgba(230, 126, 34, 0.2)', border: 'rgba(230, 126, 34, 0.4)', icon: '#E67E22' },
      red: { bg: 'rgba(231, 76, 60, 0.2)', border: 'rgba(231, 76, 60, 0.4)', icon: '#E74C3C' },
      gray: { bg: 'rgba(149, 165, 166, 0.2)', border: 'rgba(149, 165, 166, 0.4)', icon: '#95A5A6' }
    };

    const colors = colorMap[color];

    return (
      <div className="studio-card rounded-xl p-6" style={{
        background: 'rgba(52, 73, 94, 0.4)',
        border: '2px solid rgba(241, 196, 15, 0.3)',
        fontFamily: '"Share Tech Mono", monospace'
      }}>
        <div className="flex items-center">
          <div className="p-2 rounded-lg" style={{
            background: colors.bg,
            border: `1px solid ${colors.border}`
          }}>
            <Icon className="h-6 w-6" style={{ color: colors.icon }} />
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#F1C40F' }}>
              {title}
            </h3>
            <p className="text-2xl font-black" style={{
              color: '#ECF0F1',
              textShadow: '1px 1px 0px #2C3E50'
            }}>
              {value}
            </p>
            {subtitle && (
              <p className="text-xs font-bold" style={{ color: '#ECF0F1' }}>
                {subtitle}
              </p>
            )}
            {trend && (
              <div className="flex items-center mt-1">
                {trend > 0 ? (
                  <ArrowTrendingUpIcon className="h-4 w-4" style={{ color: '#2ECC71' }} />
                ) : (
                  <ArrowTrendingDownIcon className="h-4 w-4" style={{ color: '#E74C3C' }} />
                )}
                <span className={`text-xs font-bold ml-1 ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const GameTypesChart = ({ data, title = "Popular Game Types (by Plays)" }) => (
    <div className="studio-card rounded-xl p-6" style={{
      background: 'rgba(52, 73, 94, 0.4)',
      border: '2px solid rgba(241, 196, 15, 0.3)',
      fontFamily: '"Share Tech Mono", monospace'
    }}>
      <h3 className="text-lg font-black tracking-wider mb-4" style={{
        color: '#F1C40F',
        textShadow: '1px 1px 0px #E67E22'
      }}>
        {title}
      </h3>
      <div className="space-y-3">
        {data.slice(0, 5).map((gameType, index) => (
          <div key={gameType.type} className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                index === 0 ? 'bg-blue-500' :
                index === 1 ? 'bg-green-500' :
                index === 2 ? 'bg-purple-500' :
                index === 3 ? 'bg-orange-500' : 'bg-gray-400'
              }`}></div>
              <span className="text-sm font-bold capitalize" style={{ color: '#ECF0F1' }}>
                {gameType.type}
              </span>
            </div>
            <div className="text-right">
              <div className="text-sm font-black" style={{ color: '#F1C40F' }}>
                {(gameType.createdCount || 0).toLocaleString()} created
              </div>
              <div className="text-xs font-bold" style={{ color: '#ECF0F1' }}>
                {gameType.liveCount || 0} live campaigns
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const SystemHealthCard = ({ health }) => {
    const isHealthy = !health.error && health.collections;
    const totalDocuments = Object.values(health.collections || {}).reduce((sum, count) => sum + count, 0);

    return (
      <div className="studio-card rounded-xl p-6" style={{
        background: 'rgba(52, 73, 94, 0.4)',
        border: '2px solid rgba(241, 196, 15, 0.3)',
        fontFamily: '"Share Tech Mono", monospace'
      }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black tracking-wider" style={{
            color: '#F1C40F',
            textShadow: '1px 1px 0px #E67E22'
          }}>
            🏥 SYSTEM HEALTH
          </h3>
          <div className={`flex items-center px-2 py-1 rounded-full text-xs font-bold ${
            isHealthy ? 'bg-green-500/20 border border-green-500/40' : 'bg-red-500/20 border border-red-500/40'
          }`}>
            {isHealthy ? (
              <CheckCircleIcon className="h-4 w-4 mr-1" style={{ color: '#2ECC71' }} />
            ) : (
              <ExclamationTriangleIcon className="h-4 w-4 mr-1" style={{ color: '#E74C3C' }} />
            )}
            <span style={{ color: isHealthy ? '#2ECC71' : '#E74C3C' }}>
              {isHealthy ? 'HEALTHY' : 'ISSUES'}
            </span>
          </div>
        </div>

        {health.error ? (
          <div className="font-bold" style={{ color: '#E74C3C' }}>{health.error}</div>
        ) : (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-bold mb-2 uppercase tracking-wider" style={{ color: '#F1C40F' }}>
                📊 Database Collections
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(health.collections || {}).map(([collection, count]) => (
                  <div key={collection} className="flex justify-between items-center py-1">
                    <span className="text-sm font-bold capitalize" style={{ color: '#ECF0F1' }}>
                      {collection}
                    </span>
                    <span className="text-sm font-black" style={{ color: '#F1C40F' }}>
                      {count.toLocaleString()}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between items-center py-1 border-t pt-2 col-span-2" style={{
                  borderColor: 'rgba(241, 196, 15, 0.3)'
                }}>
                  <span className="text-sm font-bold" style={{ color: '#ECF0F1' }}>Total Documents</span>
                  <span className="text-sm font-black" style={{ color: '#F1C40F' }}>
                    {totalDocuments.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold mb-2 uppercase tracking-wider" style={{ color: '#F1C40F' }}>
                🧹 Storage Cleanup
              </h4>
              <div className="text-sm">
                <div className="flex justify-between">
                  <span className="font-bold" style={{ color: '#ECF0F1' }}>Files Cleaned:</span>
                  <span className="font-black" style={{ color: '#F1C40F' }}>
                    {health.storage?.totalFilesCleaned || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold" style={{ color: '#ECF0F1' }}>Last Cleanup:</span>
                  <span className="font-black" style={{ color: '#F1C40F' }}>
                    {health.storage?.lastCleanup ? 
                      new Date(health.storage.lastCleanup).toLocaleDateString() : 
                      'Never'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="studio-card rounded-xl p-6" style={{
        background: 'rgba(52, 73, 94, 0.4)',
        border: '2px solid rgba(241, 196, 15, 0.3)',
        fontFamily: '"Share Tech Mono", monospace'
      }}>
        <div className="flex items-center justify-center py-12">
          <ClockIcon className="h-8 w-8 animate-spin mr-3" style={{ color: '#F1C40F' }} />
          <span className="font-bold" style={{ color: '#ECF0F1' }}>Loading analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="studio-card rounded-xl p-6 max-w-7xl" style={{
      background: 'rgba(52, 73, 94, 0.4)',
      border: '2px solid rgba(241, 196, 15, 0.3)',
      fontFamily: '"Share Tech Mono", monospace'
    }}>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-black tracking-wider flex items-center gap-2" style={{
            color: '#F1C40F',
            textShadow: '1px 1px 0px #E67E22'
          }}>
            <ChartBarIcon className="h-6 w-6" style={{ color: '#F1C40F' }} />
            📊 ANALYTICS DASHBOARD
          </h2>
          {lastUpdated && (
            <div className="hidden sm:block text-sm font-bold" style={{ color: '#ECF0F1' }}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </div>
        
        {/* Mobile: Buttons on their own row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
          {lastUpdated && (
            <div className="block sm:hidden text-sm font-bold" style={{ color: '#ECF0F1' }}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </div>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={loadAnalytics}
              disabled={isLoading}
              className="retro-button px-3 py-1 text-sm font-bold transition-all duration-100 disabled:opacity-50"
              style={{
                background: 'rgba(52, 73, 94, 0.6)',
                border: '1px solid rgba(241, 196, 15, 0.4)',
                color: '#ECF0F1',
                borderRadius: '6px'
              }}
            >
              {isLoading ? 'Loading...' : '🔄 Quick Refresh'}
            </button>
            <button
              onClick={forceRefresh}
              disabled={isRefreshing || isLoading}
              className="retro-button px-3 py-1 text-sm font-bold transition-all duration-100 disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                border: '2px solid #F1C40F',
                color: '#ECF0F1',
                borderRadius: '6px',
                textShadow: '1px 1px 0px #A93226'
              }}
            >
              {isRefreshing ? 'Refreshing Cache...' : '🚀 Force Refresh'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-3 rounded-md flex items-start gap-2" style={{
          background: 'rgba(231, 76, 60, 0.2)',
          border: '1px solid rgba(231, 76, 60, 0.4)'
        }}>
          <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#E74C3C' }} />
          <p className="text-sm font-bold" style={{ color: '#E74C3C' }}>{error}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8" aria-label="Tabs">
          {[
            { id: 'users', name: 'Users', icon: UsersIcon },
            { id: 'campaigns', name: 'Campaigns', icon: PuzzlePieceIcon },
            { id: 'system', name: 'System', icon: ServerIcon }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-bold text-sm flex items-center gap-2 transition-all duration-100 ${
                  activeTab === tab.id
                    ? 'border-yellow-400'
                    : 'border-transparent hover:border-yellow-600/50'
                }`}
                style={{
                  color: activeTab === tab.id ? '#F1C40F' : '#ECF0F1'
                }}
              >
                <Icon className="h-4 w-4" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && userAnalytics && (
        <div className="space-y-6">
          {/* User Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Users"
              value={userAnalytics.overview.totalUsers.toLocaleString()}
              subtitle="All registered users"
              icon={UsersIcon}
              color="blue"
            />
            <StatCard
              title="Active Users"
              value={userAnalytics.overview.activeUsers.toLocaleString()}
              subtitle="Active in last 7 days"
              icon={UserGroupIcon}
              color="green"
            />
            <StatCard
              title="New This Week"
              value={userAnalytics.overview.usersLast7Days.toLocaleString()}
              subtitle="Weekly growth"
              icon={ArrowTrendingUpIcon}
              trend={userAnalytics.overview.growthRate}
              color="purple"
            />
            <StatCard
              title="Business Accounts"
              value={userAnalytics.overview.businessUsers.toLocaleString()}
              subtitle={`${userAnalytics.overview.totalCompanies} companies`}
              icon={BuildingOfficeIcon}
              color="orange"
            />
          </div>

          {/* Registration Trend */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Registration Trend</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{userAnalytics.registrationTrend.today}</div>
                <div className="text-sm text-gray-600">Today</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{userAnalytics.registrationTrend.last7Days}</div>
                <div className="text-sm text-gray-600">Last 7 Days</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{userAnalytics.registrationTrend.last30Days}</div>
                <div className="text-sm text-gray-600">Last 30 Days</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && campaignAnalytics && (
        <div className="space-y-8">
          {/* Period Selector */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-lg font-black tracking-wider" style={{
              color: '#F1C40F',
              textShadow: '1px 1px 0px #E67E22'
            }}>
              🎯 Campaign Analytics
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold" style={{ color: '#ECF0F1' }}>⏱️ Time Period:</span>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-3 py-1 text-sm font-bold transition-all duration-100 focus:outline-none"
                style={{
                  background: 'rgba(52, 73, 94, 0.6)',
                  border: '1px solid rgba(241, 196, 15, 0.4)',
                  color: '#ECF0F1',
                  borderRadius: '6px',
                  fontFamily: '"Share Tech Mono", monospace'
                }}
              >
                <option value="today" style={{ background: '#34495E', color: '#ECF0F1' }}>Today</option>
                <option value="7days" style={{ background: '#34495E', color: '#ECF0F1' }}>Last 7 Days</option>
                <option value="monthly" style={{ background: '#34495E', color: '#ECF0F1' }}>Last 30 Days</option>
                <option value="alltime" style={{ background: '#34495E', color: '#ECF0F1' }}>All Time</option>
              </select>
            </div>
          </div>

          {/* Studio Games Section */}
          <div className="space-y-6">
            <h4 className="text-xl font-black tracking-wider border-b pb-2" style={{
              color: '#F1C40F',
              textShadow: '1px 1px 0px #E67E22',
              borderColor: 'rgba(241, 196, 15, 0.3)'
            }}>
              🎮 Studio Games
            </h4>
            
            {/* Studio Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Campaigns"
                value={(campaignAnalytics.studio?.totalCampaigns || 0).toLocaleString()}
                subtitle="Studio campaigns"
                icon={DocumentTextIcon}
                color="blue"
              />
              <StatCard
                title="Total Plays"
                value={(campaignAnalytics.studio?.totalPlays || 0).toLocaleString()}
                subtitle="All studio plays"
                icon={PuzzlePieceIcon}
                color="green"
              />
              <StatCard
                title="Shares"
                value={(campaignAnalytics.studio?.totalShares || 0).toLocaleString()}
                subtitle={`${campaignAnalytics.studio?.shareRate || 0}% share rate`}
                icon={UserGroupIcon}
                color="orange"
              />
              <StatCard
                title="CTAs"
                value={(campaignAnalytics.studio?.totalCtas || 0).toLocaleString()}
                subtitle={`${campaignAnalytics.studio?.ctaRate || 0}% CTA rate`}
                icon={ArrowTrendingUpIcon}
                color="red"
              />
            </div>

            {/* Studio Game Types and Popular Campaigns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <GameTypesChart data={campaignAnalytics.studio?.gameTypes || []} title="Popular Studio Game Types" />
              
              <div className="studio-card rounded-xl p-6" style={{
                background: 'rgba(52, 73, 94, 0.4)',
                border: '2px solid rgba(241, 196, 15, 0.3)',
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                <h3 className="text-lg font-black tracking-wider mb-4" style={{
                  color: '#F1C40F',
                  textShadow: '1px 1px 0px #E67E22'
                }}>
                  🏆 Most Popular Studio Games
                </h3>
                <div className="space-y-3">
                  {(campaignAnalytics.studio?.popularCampaigns || []).slice(0, 8).map((campaign, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0" style={{
                      borderColor: 'rgba(241, 196, 15, 0.2)'
                    }}>
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="w-6 h-6 rounded flex items-center justify-center mr-3 flex-shrink-0" style={{
                          background: 'rgba(52, 152, 219, 0.3)',
                          border: '1px solid rgba(52, 152, 219, 0.5)'
                        }}>
                          <span className="text-xs font-black" style={{ color: '#3498DB' }}>{index + 1}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold truncate" style={{ color: '#ECF0F1' }}>
                            {campaign.name}
                          </div>
                          <div className="text-xs font-bold capitalize" style={{ color: '#F1C40F' }}>
                            {campaign.gameType} • ID: {campaign.id}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <div className="text-sm font-black" style={{ color: '#F1C40F' }}>
                          {campaign.plays.toLocaleString()} plays
                        </div>
                        <div className="text-xs font-bold" style={{ color: '#ECF0F1' }}>
                          {campaign.shares || 0} shares • {campaign.ctas || 0} CTAs
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Arcade Games Section */}
          <div className="space-y-6">
            <h4 className="text-xl font-black tracking-wider border-b pb-2" style={{
              color: '#F1C40F',
              textShadow: '1px 1px 0px #E67E22',
              borderColor: 'rgba(241, 196, 15, 0.3)'
            }}>
              🕹️ Arcade Games
            </h4>
            
            {/* Arcade Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <StatCard
                title="Total Games"
                value={(campaignAnalytics.arcade?.totalCampaigns || 0).toLocaleString()}
                subtitle="Public arcade games"
                icon={CpuChipIcon}
                color="purple"
              />
              <StatCard
                title="Total Plays"
                value={(campaignAnalytics.arcade?.totalPlays || 0).toLocaleString()}
                subtitle="All arcade plays"
                icon={PuzzlePieceIcon}
                color="green"
              />
              <StatCard
                title="Total Shares"
                value={(campaignAnalytics.arcade?.totalShares || 0).toLocaleString()}
                subtitle={`${campaignAnalytics.arcade?.shareRate || 0}% share rate`}
                icon={UserGroupIcon}
                color="orange"
              />
            </div>

            {/* Arcade Game Types and Popular Games */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <GameTypesChart data={campaignAnalytics.arcade?.gameTypes || []} title="Popular Arcade Game Types" />
              
              <div className="studio-card rounded-xl p-6" style={{
                background: 'rgba(52, 73, 94, 0.4)',
                border: '2px solid rgba(241, 196, 15, 0.3)',
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                <h3 className="text-lg font-black tracking-wider mb-4" style={{
                  color: '#F1C40F',
                  textShadow: '1px 1px 0px #E67E22'
                }}>
                  🕹️ Most Popular Arcade Games
                </h3>
                <div className="space-y-3">
                  {(campaignAnalytics.arcade?.popularCampaigns || []).slice(0, 8).map((campaign, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0" style={{
                      borderColor: 'rgba(241, 196, 15, 0.2)'
                    }}>
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="w-6 h-6 rounded flex items-center justify-center mr-3 flex-shrink-0" style={{
                          background: 'rgba(155, 89, 182, 0.3)',
                          border: '1px solid rgba(155, 89, 182, 0.5)'
                        }}>
                          <span className="text-xs font-black" style={{ color: '#9B59B6' }}>{index + 1}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold truncate" style={{ color: '#ECF0F1' }}>
                            {campaign.name}
                          </div>
                          <div className="text-xs font-bold capitalize" style={{ color: '#F1C40F' }}>
                            {campaign.gameType} • ID: {campaign.id}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <div className="text-sm font-black" style={{ color: '#F1C40F' }}>
                          {campaign.plays.toLocaleString()} plays
                        </div>
                        <div className="text-xs font-bold" style={{ color: '#ECF0F1' }}>
                          {campaign.shares || 0} shares
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Tab */}
      {activeTab === 'system' && systemHealth && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <SystemHealthCard health={systemHealth} />
            
            {systemHealth.storage?.recentCleanups && (
              <div className="studio-card rounded-xl p-6 lg:col-span-2" style={{
                background: 'rgba(52, 73, 94, 0.4)',
                border: '2px solid rgba(241, 196, 15, 0.3)',
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                <h3 className="text-lg font-black tracking-wider mb-4" style={{
                  color: '#F1C40F',
                  textShadow: '1px 1px 0px #E67E22'
                }}>
                  🧹 Recent Storage Cleanups
                </h3>
                <div className="space-y-3">
                  {systemHealth.storage.recentCleanups.slice(0, 5).map((cleanup, index) => (
                    <div key={cleanup.id || index} className="flex items-center justify-between py-2 border-b last:border-b-0" style={{
                      borderColor: 'rgba(241, 196, 15, 0.2)'
                    }}>
                      <div>
                        <div className="text-sm font-bold" style={{ color: '#ECF0F1' }}>
                          {cleanup.filesDeleted || 0} files deleted
                        </div>
                        <div className="text-xs font-bold" style={{ color: '#F1C40F' }}>
                          {cleanup.timestamp instanceof Date 
                            ? cleanup.timestamp.toLocaleString()
                            : new Date(cleanup.timestamp).toLocaleString()
                          }
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold" style={{ color: '#ECF0F1' }}>
                          {cleanup.totalFilesScanned || 0} scanned
                        </div>
                        {cleanup.errors > 0 && (
                          <div className="text-xs font-bold" style={{ color: '#E74C3C' }}>
                            {cleanup.errors} errors
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsWidget; 